import React from 'react';
import { Select, InputNumber, Space, Card, Switch, Tag } from 'antd';

const { Option } = Select;

const revenueCatStatuses = [
  'INITIAL_PURCHASE',
  'RENEWAL',
  'CANCELLATION',
  'UNCANCELLATION',
  'NON_RENEWING_PURCHASE',
  'SUBSCRIPTION_PAUSED',
  'EXPIRATION',
  'BILLING_ISSUE',
  'PRODUCT_CHANGE',
  'TRANSFER',
  'SUBSCRIPTION_EXTENDED',
  'TEMPORARY_ENTITLEMENT_GRANT',
  'REFUND_REVERSED',
];

const statusColors = {
  INITIAL_PURCHASE: 'green',
  RENEWAL: 'green',
  UNCANCELLATION: 'green',
  NON_RENEWING_PURCHASE: 'blue',
  SUBSCRIPTION_EXTENDED: 'green',
  CANCELLATION: 'red',
  EXPIRATION: 'red',
  BILLING_ISSUE: 'orange',
  SUBSCRIPTION_PAUSED: 'orange',
  PRODUCT_CHANGE: 'purple',
  TRANSFER: 'purple',
  TEMPORARY_ENTITLEMENT_GRANT: 'cyan',
  REFUND_REVERSED: 'red',
};

const operatorOptions = [
  { value: 'gt', label: '>' },
  { value: 'lt', label: '<' },
  { value: 'gte', label: '>=' },
  { value: 'lte', label: '<=' },
  { value: 'eq', label: '=' },
];

const userTypeOptions = [
  { value: 'pro', label: 'Pro Users', color: 'green' },
  { value: 'free', label: 'Free Users', color: 'orange' },
];

// Country codes extracted from client locale header (e.g. "en_US" → "US")
const countryOptions = [
  { value: 'US', label: 'United States' },
  { value: 'GB', label: 'United Kingdom' },
  { value: 'CA', label: 'Canada' },
  { value: 'AU', label: 'Australia' },
  { value: 'TR', label: 'Turkey' },
  { value: 'BR', label: 'Brazil' },
  { value: 'MX', label: 'Mexico' },
  { value: 'AR', label: 'Argentina' },
  { value: 'ES', label: 'Spain' },
  { value: 'FR', label: 'France' },
  { value: 'DE', label: 'Germany' },
  { value: 'IT', label: 'Italy' },
  { value: 'NL', label: 'Netherlands' },
  { value: 'PT', label: 'Portugal' },
  { value: 'JP', label: 'Japan' },
  { value: 'KR', label: 'South Korea' },
  { value: 'CN', label: 'China' },
  { value: 'TW', label: 'Taiwan' },
  { value: 'HK', label: 'Hong Kong' },
  { value: 'IN', label: 'India' },
  { value: 'SA', label: 'Saudi Arabia' },
  { value: 'AE', label: 'UAE' },
  { value: 'RU', label: 'Russia' },
  { value: 'UA', label: 'Ukraine' },
  { value: 'PL', label: 'Poland' },
  { value: 'SE', label: 'Sweden' },
  { value: 'DK', label: 'Denmark' },
  { value: 'NO', label: 'Norway' },
  { value: 'FI', label: 'Finland' },
  { value: 'TH', label: 'Thailand' },
  { value: 'VN', label: 'Vietnam' },
  { value: 'ID', label: 'Indonesia' },
  { value: 'MY', label: 'Malaysia' },
  { value: 'PH', label: 'Philippines' },
  { value: 'RO', label: 'Romania' },
  { value: 'GR', label: 'Greece' },
  { value: 'IL', label: 'Israel' },
  { value: 'CZ', label: 'Czech Republic' },
  { value: 'HU', label: 'Hungary' },
  { value: 'CO', label: 'Colombia' },
  { value: 'CL', label: 'Chile' },
  { value: 'PE', label: 'Peru' },
  { value: 'EG', label: 'Egypt' },
  { value: 'NG', label: 'Nigeria' },
  { value: 'ZA', label: 'South Africa' },
  { value: 'PK', label: 'Pakistan' },
  { value: 'BD', label: 'Bangladesh' },
  { value: 'SG', label: 'Singapore' },
  { value: 'IE', label: 'Ireland' },
  { value: 'NZ', label: 'New Zealand' },
  { value: 'AT', label: 'Austria' },
  { value: 'CH', label: 'Switzerland' },
  { value: 'BE', label: 'Belgium' },
];

const TargetingRulesEditor = ({ value, onChange }) => {
  const isAdvanced = !!value;
  const rules = value || {};

  const handleToggle = (checked) => {
    onChange(checked ? {} : null);
  };

  const updateRules = (patch) => {
    const updated = { ...rules, ...patch };
    // Clean up empty/null keys
    if (updated.userTypes && updated.userTypes.length === 0) delete updated.userTypes;
    if (updated.revenueCatStatuses && updated.revenueCatStatuses.length === 0) delete updated.revenueCatStatuses;
    if (updated.locations && updated.locations.length === 0) delete updated.locations;
    if (updated.creditConditions && updated.creditConditions.length === 0) delete updated.creditConditions;
    onChange(updated);
  };

  return (
    <div>
      <Space align="center" style={{ marginBottom: isAdvanced ? 12 : 0 }}>
        <Switch size="small" checked={isAdvanced} onChange={handleToggle} />
        <span style={{ fontSize: 13, color: '#666' }}>
          {isAdvanced ? 'Advanced targeting enabled' : 'No targeting rules (show to all)'}
        </span>
      </Space>

      {isAdvanced && (
        <Card size="small" style={{ background: '#fafafa' }}>
          {/* User Type */}
          <div style={{ marginBottom: 16 }}>
            <div style={sectionLabel}>User Type</div>
            <Select
              mode="multiple"
              placeholder="All users (no filter)"
              value={rules.userTypes || []}
              onChange={(val) => updateRules({ userTypes: val })}
              style={{ width: '100%' }}
              tagRender={({ label, value: tagValue, closable, onClose }) => {
                const opt = userTypeOptions.find((o) => o.value === tagValue);
                return (
                  <Tag color={opt?.color || 'default'} closable={closable} onClose={onClose} style={{ marginRight: 3 }}>
                    {label}
                  </Tag>
                );
              }}
            >
              {userTypeOptions.map((o) => (
                <Option key={o.value} value={o.value}>{o.label}</Option>
              ))}
            </Select>
          </div>

          {/* RevenueCat Status */}
          <div style={{ marginBottom: 16 }}>
            <div style={sectionLabel}>RevenueCat Status</div>
            <Select
              mode="multiple"
              placeholder="Any status (no filter)"
              value={rules.revenueCatStatuses || []}
              onChange={(val) => updateRules({ revenueCatStatuses: val })}
              style={{ width: '100%' }}
              tagRender={({ label, closable, onClose }) => (
                <Tag color={statusColors[label] || 'default'} closable={closable} onClose={onClose} style={{ marginRight: 3 }}>
                  {label}
                </Tag>
              )}
            >
              {revenueCatStatuses.map((s) => (
                <Option key={s} value={s}>{s}</Option>
              ))}
            </Select>
          </div>

          {/* Credit Conditions */}
          <div style={{ marginBottom: 16 }}>
            <div style={sectionLabel}>Credit Conditions</div>
            {(rules.creditConditions || []).map((cond, i) => (
              <Space key={i} style={{ marginBottom: 6 }}>
                <span style={{ fontSize: 13 }}>Credits</span>
                <Select
                  value={cond.operator}
                  onChange={(op) => {
                    const updated = [...(rules.creditConditions || [])];
                    updated[i] = { ...updated[i], operator: op };
                    updateRules({ creditConditions: updated });
                  }}
                  size="small"
                  style={{ width: 70 }}
                >
                  {operatorOptions.map((o) => (
                    <Option key={o.value} value={o.value}>{o.label}</Option>
                  ))}
                </Select>
                <InputNumber
                  value={cond.value}
                  onChange={(val) => {
                    const updated = [...(rules.creditConditions || [])];
                    updated[i] = { ...updated[i], value: val };
                    updateRules({ creditConditions: updated });
                  }}
                  min={0}
                  size="small"
                  style={{ width: 80 }}
                />
                <a
                  style={{ color: '#ff4d4f', fontSize: 12 }}
                  onClick={() => {
                    const updated = (rules.creditConditions || []).filter((_, j) => j !== i);
                    updateRules({ creditConditions: updated });
                  }}
                >
                  Remove
                </a>
              </Space>
            ))}
            <div>
              <a
                style={{ fontSize: 12 }}
                onClick={() => {
                  const updated = [...(rules.creditConditions || []), { operator: 'gt', value: 0 }];
                  updateRules({ creditConditions: updated });
                }}
              >
                + Add condition
              </a>
            </div>
            {(rules.creditConditions || []).length > 0 && (
              <div style={{ fontSize: 11, color: '#aaa', marginTop: 4 }}>
                All conditions must match (AND).
              </div>
            )}
          </div>

          {/* Country */}
          <div>
            <div style={sectionLabel}>Country</div>
            <Select
              mode="multiple"
              placeholder="All countries (no filter)"
              value={rules.locations || []}
              onChange={(val) => updateRules({ locations: val })}
              style={{ width: '100%' }}
              showSearch
              optionFilterProp="label"
              tagRender={({ value: tagValue, closable, onClose }) => {
                const c = countryOptions.find((o) => o.value === tagValue);
                return (
                  <Tag color="cyan" closable={closable} onClose={onClose} style={{ marginRight: 3 }}>
                    {tagValue} — {c?.label || tagValue}
                  </Tag>
                );
              }}
            >
              {countryOptions.map((c) => (
                <Option key={c.value} value={c.value} label={`${c.value} ${c.label}`}>
                  {c.value} — {c.label}
                </Option>
              ))}
            </Select>
          </div>
        </Card>
      )}
    </div>
  );
};

const sectionLabel = {
  fontSize: 12,
  fontWeight: 600,
  color: '#888',
  marginBottom: 6,
  textTransform: 'uppercase',
};

// Summary renderer for list views
export const TargetingRulesSummary = ({ targetingRules, audienceType }) => {
  if (!targetingRules) {
    return <Tag>{audienceType || 'ALL'}</Tag>;
  }

  const tags = [];

  if (targetingRules.userTypes?.length) {
    targetingRules.userTypes.forEach((t) => {
      const opt = userTypeOptions.find((o) => o.value === t);
      tags.push(<Tag key={`ut-${t}`} color={opt?.color || 'default'}>{opt?.label || t}</Tag>);
    });
  }

  if (targetingRules.revenueCatStatuses?.length) {
    tags.push(
      <Tag key="rc" color="blue">
        RC: {targetingRules.revenueCatStatuses.join(', ')}
      </Tag>
    );
  }

  if (targetingRules.creditConditions?.length) {
    const parts = targetingRules.creditConditions.map((c) => {
      const op = operatorOptions.find((o) => o.value === c.operator);
      return `${op?.label || c.operator} ${c.value}`;
    });
    tags.push(
      <Tag key="credit" color="purple">
        Credits: {parts.join(' & ')}
      </Tag>
    );
  }

  if (targetingRules.locations?.length) {
    const labels = targetingRules.locations.map((code) => {
      const c = countryOptions.find((o) => o.value === code);
      return c ? c.label : code;
    });
    tags.push(
      <Tag key="loc" color="cyan">
        {labels.join(', ')}
      </Tag>
    );
  }

  if (tags.length === 0) {
    return <Tag>ALL (advanced)</Tag>;
  }

  return <Space size={4} wrap>{tags}</Space>;
};

export default TargetingRulesEditor;
